import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Chip,
  Paper,
  TextField,
  Typography,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import ViewModuleOutlinedIcon from "@mui/icons-material/ViewModuleOutlined";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import { GraphQLClient } from "graphql-request";

import Header from "../../components/Header";
import TableActions from "../../components/TableActions";
import ActionButton from "../../components/ActionButton";
import ThemedDataGrid from "../../components/ThemedDataGrid";
import DeletePlaybookModal from "../../components/DeletePlaybookModal";
import { useTenant } from "../../context/TenantContext";
import { useSnackbar } from "../../components/snackbarProvider.jsx";

import { useTheme } from "@mui/material/styles";
import { tokens } from "../../theme/tokens";

import { graphqlEndpoint } from "../../config";
import { READ_PLAYBOOKS } from "../../graph/playbooks/queries";
import { DELETE_PLAYBOOK } from "../../graph/playbooks/mutations";

const client = new GraphQLClient(graphqlEndpoint);

const isDeliveryMethod = (playbook) =>
  playbook.name?.trim().toLowerCase() ===
  "operational clarity delivery method";

const ManagePlaybooks = () => {
  const navigate = useNavigate();
  const { tenantId, tenantName } = useTenant();
  const { setSnackbar } = useSnackbar();

  const [playbooks, setPlaybooks] = useState([]);
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState("cards");
  const [loading, setLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPlaybook, setSelectedPlaybook] = useState(null);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const fetchPlaybooks = useCallback(async () => {
    if (!tenantId) {
      setPlaybooks([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const data = await client.request(READ_PLAYBOOKS, { tenantId });

      const formattedPlaybooks = (data.readPlaybooks || []).map(
        (playbook) => ({
          ...playbook,
          id: playbook.id,
          itemCount: playbook.items?.length || 0,
          transitionCount: playbook.transitions?.length || 0,
        })
      );

      setPlaybooks(formattedPlaybooks);
    } catch (err) {
      console.error("Failed to fetch playbooks:", err);
      setPlaybooks([]);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to load playbooks",
      });
    } finally {
      setLoading(false);
    }
  }, [tenantId, setSnackbar]);

  useEffect(() => {
    fetchPlaybooks();
  }, [fetchPlaybooks]);

  const handleOpenDeleteModal = useCallback((playbook) => {
    setSelectedPlaybook(playbook);
    setDeleteModalOpen(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setDeleteModalOpen(false);
    setSelectedPlaybook(null);
  }, []);

  const handleConfirmDelete = async () => {
    if (!selectedPlaybook?.id) return;

    try {
      await client.request(DELETE_PLAYBOOK, {
        id: selectedPlaybook.id,
      });
      await fetchPlaybooks();
      handleCloseDeleteModal();

      setSnackbar({
        open: true,
        severity: "success",
        message: "Playbook deleted successfully",
      });
    } catch (err) {
      console.error("Failed to delete playbook:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to delete playbook",
      });
    }
  };

  const filteredPlaybooks = useMemo(() => {
    const searchTerm = search.trim().toLowerCase();

    return playbooks
      .filter((playbook) => playbook.tenantId === tenantId)
      .filter((playbook) => {
        if (!searchTerm) return true;

        return (
          playbook.name?.toLowerCase().includes(searchTerm) ||
          playbook.objective?.toLowerCase().includes(searchTerm)
        );
      })
      .sort((a, b) => {
        if (isDeliveryMethod(a)) return -1;
        if (isDeliveryMethod(b)) return 1;
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [playbooks, search, tenantId]);

  const openPlaybook = useCallback(
    (playbook, mode = "read") => {
      navigate(`/playbooks/${mode}/${playbook.id}`, {
        state: { redirectPath: "/managePlaybooks" },
      });
    },
    [navigate]
  );

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1.2,
        minWidth: 220,
        cellClassName: "name-column--cell",
        renderCell: ({ row }) => (
          <Box display="flex" alignItems="center" gap={1} minWidth={0}>
            <Typography noWrap>
              {row.name || "Untitled playbook"}
            </Typography>
            {isDeliveryMethod(row) && (
              <Chip
                size="small"
                color="secondary"
                label="Start here"
              />
            )}
          </Box>
        ),
      },
      {
        field: "objective",
        headerName: "Objective",
        flex: 1.6,
        minWidth: 220,
        valueGetter: (value) => value || "—",
      },
      {
        field: "itemCount",
        headerName: "Items",
        flex: 0.8,
        minWidth: 110,
        renderCell: ({ row }) => (
          <Chip
            icon={<AccountTreeOutlinedIcon />}
            label={row.itemCount || 0}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        field: "transitionCount",
        headerName: "Transitions",
        flex: 0.9,
        minWidth: 130,
        renderCell: ({ row }) => (
          <Chip
            icon={<RouteOutlinedIcon />}
            label={row.transitionCount || 0}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        field: "updatedAt",
        headerName: "Updated",
        flex: 1,
        minWidth: 170,
        valueFormatter: (value) =>
          value ? new Date(value).toLocaleString() : "—",
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 1.2,
        minWidth: 170,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => (
          <TableActions
            onView={() => openPlaybook(row, "read")}
            onEdit={() => openPlaybook(row, "edit")}
            onDelete={() => handleOpenDeleteModal(row)}
            hideCreate
          />
        ),
      },
    ],
    [handleOpenDeleteModal, openPlaybook]
  );

  const handleViewModeChange = (_event, nextViewMode) => {
    if (nextViewMode) {
      setViewMode(nextViewMode);
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
        mb={3}
      >
        <Header
          title="PLAYBOOK LIBRARY"
          subtitle={
            tenantName
              ? `Browse and manage playbooks for ${tenantName}`
              : "Browse and manage tenant playbooks"
          }
        />

        <ActionButton
          tone="gold"
          onClick={() =>
            navigate("/createPlaybook", {
              state: { redirectPath: "/managePlaybooks" },
            })
          }
          disabled={!tenantId}
        >
          Create Playbook
        </ActionButton>
      </Box>

      {!tenantId ? (
        <Box mt={4}>
          <Typography variant="h6">
            Select a tenant in the top bar to view playbooks.
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            flexWrap="wrap"
            mb={3}
          >
            <TextField
              size="small"
              placeholder="Search playbooks by name or objective"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ width: "100%", maxWidth: 560 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlinedIcon />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              size="small"
              aria-label="Playbook display mode"
            >
              <ToggleButton value="cards" aria-label="Card view">
                <ViewModuleOutlinedIcon sx={{ mr: 0.75 }} />
                Cards
              </ToggleButton>
              <ToggleButton value="table" aria-label="Table view">
                <TableRowsOutlinedIcon sx={{ mr: 0.75 }} />
                Table
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {loading ? (
            <Typography color="text.secondary">
              Loading playbooks…
            </Typography>
          ) : filteredPlaybooks.length === 0 ? (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6">
                {search ? "No matching playbooks" : "No playbooks yet"}
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                {search
                  ? "Try a different search term."
                  : "Create a playbook to start building your method."}
              </Typography>
            </Paper>
          ) : viewMode === "table" ? (
            <ThemedDataGrid
              rows={filteredPlaybooks}
              columns={columns}
            />
          ) : (
            <Box
              display="grid"
              gap={2}
              gridTemplateColumns="repeat(auto-fill, minmax(300px, 1fr))"
            >
              {filteredPlaybooks.map((playbook) => {
                const isOverview = isDeliveryMethod(playbook);

                return (
                  <Paper
                    key={playbook.id}
                    variant="outlined"
                    sx={{
                      p: 2.25,
                      display: "flex",
                      flexDirection: "column",
                      minHeight: 250,
                      borderColor: isOverview
                        ? "secondary.main"
                        : "divider",
                    }}
                  >
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      gap={1}
                      mb={1}
                    >
                      <Typography
                        variant="h6"
                        component="h2"
                        sx={{
                          mb: 0.5,
                          fontWeight: 700,
                          color: colors.scalpelTeal[400],
                        }}
                      >
                        {playbook.name || "Untitled playbook"}
                      </Typography>

                      {isOverview && (
                        <Chip
                          size="small"
                          color="secondary"
                          label="Start here"
                        />
                      )}
                    </Box>

                    <Typography
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        display: "-webkit-box",
                        WebkitLineClamp: 4,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                      }}
                    >
                      {playbook.objective || "No objective added yet."}
                    </Typography>

                    <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<AccountTreeOutlinedIcon />}
                        label={`${playbook.itemCount} ${
                          playbook.itemCount === 1 ? "item" : "items"
                        }`}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<RouteOutlinedIcon />}
                        label={`${playbook.transitionCount} ${
                          playbook.transitionCount === 1
                            ? "transition"
                            : "transitions"
                        }`}
                      />
                    </Box>

                    <Box
                      mt="auto"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={1}
                    >
                      <Chip
                        size="small"
                        variant="outlined"
                        icon={<MenuBookOutlinedIcon />}
                        label="Playbook"
                      />

                      <TableActions
                        onView={() => openPlaybook(playbook, "read")}
                        onEdit={() => openPlaybook(playbook, "edit")}
                        onDelete={() =>
                          handleOpenDeleteModal(playbook)
                        }
                        hideCreate
                      />
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          )}
        </>
      )}

      <DeletePlaybookModal
        open={deleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        playbook={
          selectedPlaybook
            ? {
                name: selectedPlaybook.name,
                objective: selectedPlaybook.objective || "",
              }
            : null
        }
      />
    </Box>
  );
};

export default ManagePlaybooks;